import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post, put } from 'utils/api';
import { logError } from 'utils/log';

import type {
  Dispatch,
  ThunkAction,
  TypedAction
} from 'types';
import type {
  Report,
  ReportState
} from 'modules/insights/reports/types';

const { INSIGHTS_REPORTS } = API.END_POINTS;

const REPORTS_PROCESSING = 'insights-reports/REPORTS_PROCESSING';
const REPORTS_LOADED = 'insights-reports/REPORTS_LOADED';
const REPORTS_RESET = 'insights-reports/REPORTS_RESET';
const REPORT_LOADED = 'insights-reports/REPORT_LOADED';
const REPORT_CREATED = 'insights-reports/REPORT_CREATED';
const REPORT_UPDATED = 'insights-reports/REPORT_UPDATED';
const REPORT_DUPLICATED = 'insights-reports/REPORT_DUPLICATED';
const REPORT_DELETED = 'insights-reports/REPORT_DELETED';
const REPORT_RESET = 'insights-reports/REPORT_RESET';

const initialState: ReportState = {
  reports: [],
  report: {} as Report,
  isLoading: false
};

type Status = {
  status: boolean
};

const setStatus = (
  status: boolean
): TypedAction<Status> => ({
  type: REPORTS_PROCESSING,
  payload: {
    status
  }
});

type Reports = {
  reports: Array<Report>
};

const reportsLoaded = (
  reports: Array<Report>
): TypedAction<Reports> => ({
  type: REPORTS_LOADED,
  payload: {
    reports
  }
});

type ReportSingle = {
  report: Report
};

const reportLoaded = (
  report: Report
): TypedAction<ReportSingle> => ({
  type: REPORT_LOADED,
  payload: {
    report
  }
});

const reportCreated = (
  report: Report
): TypedAction<ReportSingle> => ({
  type: REPORT_CREATED,
  payload: {
    report
  }
});

const reportUpdated = (
  report: Report
): TypedAction<ReportSingle> => ({
  type: REPORT_UPDATED,
  payload: {
    report
  }
});

const reportDuplicated = (
  report: Report
): TypedAction<ReportSingle> => ({
  type: REPORT_DUPLICATED,
  payload: {
    report
  }
});

type ReportId = {
  reportId: number
};

const reportDeleted = (
  reportId: number
): TypedAction<ReportId> => ({
  type: REPORT_DELETED,
  payload: {
    reportId
  }
});

type Reset = {
  type: typeof REPORT_RESET
};

export const resetReports = (): TypedAction<Reset> => ({
  type: REPORTS_RESET
});

export const resetReport = (): TypedAction<Reset> => ({
  type: REPORT_RESET
});

export const fetchReports = (
  brandProfileId: number
): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  try {
    const url = INSIGHTS_REPORTS.getReports(brandProfileId);
    const response = await get(url);
    const reports = response.data || [];

    return dispatch(reportsLoaded(reports));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const fetchReport = (
  brandProfileId: number,
  reportId: number
): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  try {
    const url = INSIGHTS_REPORTS.getReportById(brandProfileId, reportId);
    const response = await get(url);
    const report = response.data || [];

    return dispatch(reportLoaded(report));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const createReport = (
  report: Report
): ThunkAction => (
  async (dispatch: Dispatch) => {
    const url = INSIGHTS_REPORTS.createReport(report.brandProfileId);

    dispatch(showLoading(url));

    try {
      const response = await post(url, { data: report });
      const newReport = response.data || {};

      dispatch(reportCreated(newReport));

      return newReport;
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(hideLoading(url));
    }
  }
);

export const updateReport = (
  report: Report
): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  try {
    const url = INSIGHTS_REPORTS.updateReport(report.id, report.brandProfileId);
    const response = await put(url, { data: report });
    const updatedReport = response.data || {};

    return dispatch(reportUpdated(updatedReport));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const duplicateReport = (
  report: Report
): ThunkAction => async (dispatch: Dispatch) => {
  const url = INSIGHTS_REPORTS.cloneReport(report?.id);

  dispatch(setStatus(true));

  try {
    const response = await post(url, { data: report });
    const newReport = response.data || {};

    dispatch(reportDuplicated(newReport));

    return newReport;
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const deleteReport = (
  brandProfileId: number,
  reportId: number
): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  try {
    const url = INSIGHTS_REPORTS.deleteReport(brandProfileId, reportId);
    await destroy(url);

    return dispatch(reportDeleted(reportId));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

const handleProcessing = (
  state: ReportState,
  { status }: Status
): ReportState => ({
  ...state,
  isLoading: status
});

const handleLoadedReports = (
  state: ReportState,
  { reports }: Reports
): ReportState => ({
  ...state,
  reports,
  isLoading: false
});

const handleLoadedReport = (
  state: ReportState,
  { report }: ReportSingle
): ReportState => ({
  ...state,
  report,
  isLoading: false
});

const handleCreated = (
  state: ReportState,
  { report }: ReportSingle
): ReportState => ({
  ...state,
  report,
  reports: [
    ...state.reports,
    report
  ],
  isLoading: false
});

const handleUpdated = (
  state: ReportState,
  { report }: ReportSingle
): ReportState => ({
  ...state,
  report,
  isLoading: false
});

const handleDeleted = (
  state: ReportState,
  { reportId }: ReportId
): ReportState => ({
  ...state,
  reports: state.reports.filter(report => report.id !== reportId),
  isLoading: false
});

const handleResetReports = (
  state: ReportState
): ReportState => ({
  ...state,
  reports: initialState.reports,
  isLoading: false
});

const handleResetReport = (
  state: ReportState
): ReportState => ({
  ...state,
  report: initialState.report,
  isLoading: false
});

export default createReducer<ReportState, any>('insightsReports', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [REPORTS_PROCESSING]: handleProcessing,
  [REPORTS_LOADED]: handleLoadedReports,
  [REPORTS_RESET]: handleResetReports,
  [REPORT_LOADED]: handleLoadedReport,
  [REPORT_CREATED]: handleCreated,
  [REPORT_UPDATED]: handleUpdated,
  [REPORT_DUPLICATED]: handleCreated,
  [REPORT_DELETED]: handleDeleted,
  [REPORT_RESET]: handleResetReport
});
